export type BalanceChangeReason = 'local-action' | 'passive-refresh';

export const BALANCE_SYNC_NAMESPACE = 'parlaygames.balance-sync';
export const BALANCE_SYNC_CHANNEL_NAME = 'parlaygames';
export const BALANCE_SYNC_BRIDGE_URL = '/iframe-bridge.html';

type BridgeMessageType = 'bridge:connect' | 'bridge:ready' | 'balance:changed';

export type BalanceBridgeMessage = {
    namespace: typeof BALANCE_SYNC_NAMESPACE;
    type: BridgeMessageType;
    sourceId?: string;
    appId?: string;
    reason?: BalanceChangeReason;
    timestamp?: number;
};

type BridgeListener = (message: BalanceBridgeMessage) => void;

type CreateBalanceSyncBridgeOptions = {
    appId: string;
    bridgeUrl?: string;
    sourceId?: string;
};

const isBalanceBridgeMessage = (
    data: unknown
): data is BalanceBridgeMessage => {
    if (!data || typeof data !== 'object') {
        return false;
    }

    const message = data as Partial<BalanceBridgeMessage>;

    if (
        message.namespace !== BALANCE_SYNC_NAMESPACE ||
        (message.type !== 'bridge:connect' &&
            message.type !== 'bridge:ready' &&
            message.type !== 'balance:changed')
    ) {
        return false;
    }

    if (message.type !== 'balance:changed') {
        return true;
    }

    return (
        typeof message.sourceId === 'string' &&
        typeof message.appId === 'string' &&
        (message.reason === undefined ||
            message.reason === 'local-action' ||
            message.reason === 'passive-refresh')
    );
};

const createSourceId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const createHiddenIframe = (bridgeUrl: string) => {
    const iframe = document.createElement('iframe');

    iframe.src = bridgeUrl;
    iframe.setAttribute('aria-hidden', 'true');
    iframe.tabIndex = -1;
    iframe.style.position = 'absolute';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.opacity = '0';
    iframe.style.pointerEvents = 'none';

    return iframe;
};

export const createBalanceSyncBridge = ({
    appId,
    bridgeUrl = BALANCE_SYNC_BRIDGE_URL,
    sourceId = createSourceId(),
}: CreateBalanceSyncBridgeOptions) => {
    const resolvedBridgeUrl = new URL(bridgeUrl, window.location.href);
    const bridgeOrigin = resolvedBridgeUrl.origin;
    const iframe = createHiddenIframe(resolvedBridgeUrl.toString());
    const listeners = new Set<BridgeListener>();
    const queue: BalanceBridgeMessage[] = [];
    let isReady = false;
    let isDestroyed = false;

    const postToBridge = (message: BalanceBridgeMessage) => {
        if (isDestroyed) {
            return;
        }

        const bridgeWindow = iframe.contentWindow;

        if (!bridgeWindow || !isReady) {
            queue.push(message);
            return;
        }

        bridgeWindow.postMessage(message, bridgeOrigin);
    };

    const flushQueue = () => {
        if (!isReady || isDestroyed || !iframe.contentWindow) {
            return;
        }

        const pending = queue.splice(0, queue.length);

        for (const message of pending) {
            iframe.contentWindow.postMessage(message, bridgeOrigin);
        }
    };

    const postConnect = () => {
        postToBridge({
            namespace: BALANCE_SYNC_NAMESPACE,
            type: 'bridge:connect',
            appId,
            sourceId,
            timestamp: Date.now(),
        });
    };

    const handleMessage = (event: MessageEvent<unknown>) => {
        if (
            event.origin !== bridgeOrigin ||
            event.source !== iframe.contentWindow
        ) {
            return;
        }

        if (!isBalanceBridgeMessage(event.data)) {
            return;
        }

        if (event.data.type === 'bridge:ready') {
            isReady = true;
            flushQueue();
            return;
        }

        if (event.data.type !== 'balance:changed') {
            return;
        }

        for (const listener of listeners) {
            listener(event.data);
        }
    };

    const handleLoad = () => {
        postConnect();
    };

    window.addEventListener('message', handleMessage);
    iframe.addEventListener('load', handleLoad);
    document.body.appendChild(iframe);

    return {
        sourceId,
        addEventListener: (listener: BridgeListener) => {
            listeners.add(listener);

            return () => {
                listeners.delete(listener);
            };
        },
        postBalanceChange: (reason: BalanceChangeReason) => {
            postToBridge({
                namespace: BALANCE_SYNC_NAMESPACE,
                type: 'balance:changed',
                sourceId,
                appId,
                reason,
                timestamp: Date.now(),
            });
        },
        destroy: () => {
            if (isDestroyed) {
                return;
            }

            isDestroyed = true;
            queue.length = 0;
            listeners.clear();
            window.removeEventListener('message', handleMessage);
            iframe.removeEventListener('load', handleLoad);
            iframe.remove();
        },
    };
};
